// TNBEWEB-1945 — rich-text normalisation for the screen-editor Text node.
//
// The editor authors HTML (format menu + hand-typed tags) and stores it on the
// node's `value`. This module is the runtime half of that contract and MUST
// stay in sync with the editor's `components/HtmlContent/sanitize.ts`:
// same allowlist, same literal-tag decoding, same whitespace encoding — so the
// canvas, the preview iframe and the published screen render identically.

import DOMPurify from 'dompurify';

export const ALLOWED_TAGS = [
  'strong', 'em', 'b', 'i', 'u', 's', 'mark', 'small', 'sup', 'sub', 'pre', 'dfn',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'q', 'blockquote', 'cite',
  'code', 'samp', 'kbd', 'var', 'time',
  'a', 'hr', 'br', 'ul', 'ol', 'li',
  'abbr', 'ins', 'del',
  'details', 'summary',
  'span',
];

const ALLOWED_ATTR = ['href', 'target', 'rel', 'datetime', 'title', 'open', 'style'];

// `style` survives only for safe character properties — `url(...)` /
// `background-image` are an XSS and data-exfiltration vector. DOMPurify has no
// per-property style filter, so the declaration list is re-parsed by hand.
const SAFE_STYLE_PROPS = new Set([
  'color', 'background-color',
  'font-weight', 'font-style', 'text-decoration', 'text-decoration-line',
  'font-size', 'font-family', 'letter-spacing',
]);
const VALUE_HAS_FORBIDDEN = /url\s*\(|expression\s*\(|image-set\s*\(/i;

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (!(node instanceof HTMLElement)) return;
  const styleAttr = node.getAttribute('style');
  if (!styleAttr) return;
  const safe = styleAttr
    .split(';')
    .map((decl) => {
      const idx = decl.indexOf(':');
      if (idx === -1) return null;
      const prop = decl.slice(0, idx).trim().toLowerCase();
      const value = decl.slice(idx + 1).trim();
      if (!SAFE_STYLE_PROPS.has(prop) || !value || VALUE_HAS_FORBIDDEN.test(value)) return null;
      return `${prop}: ${value}`;
    })
    .filter(Boolean)
    .join('; ');

  if (safe) node.setAttribute('style', safe);
  else node.removeAttribute('style');
});

// Authored links must be usable in the preview iframe and in the published
// screen. Two things stopped them working: a href typed without a scheme
// ("google.com") resolves against the app origin, and an in-place navigation
// inside the preview iframe just replaces the preview. Normalise the scheme and
// send every link to a new tab. Done in a sanitizer hook rather than in JSX
// because the ellipsis path renders the content to a STRING
// (ReactDOMServer.renderToString) — React event handlers do not survive that,
// plain attributes do.
const HAS_SCHEME_RE = /^([a-z][a-z0-9+.-]*:|\/\/|\/|#|\?)/i;

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (!(node instanceof HTMLElement) || node.tagName !== 'A') return;
  const href = node.getAttribute('href');
  if (!href) return;
  const trimmed = href.trim();
  if (trimmed && !HAS_SCHEME_RE.test(trimmed)) node.setAttribute('href', `https://${trimmed}`);
  node.setAttribute('target', '_blank');
  node.setAttribute('rel', 'noopener noreferrer');
});

const ALLOWED_TAG_SET = new Set(ALLOWED_TAGS);

// The editor's contentEditable escapes everything the user types, so markup
// written by hand arrives as `&lt;b&gt;bold&lt;/b&gt;` and would render as literal
// text. Decode it — but only for allowlisted tags, so `&lt;script&gt;` and any
// prose containing `<` stays inert.
const LITERAL_TAG_RE = /&lt;(\/?)([a-zA-Z][a-zA-Z0-9]*)([^<>]*?)&gt;/g;

export const decodeLiteralTags = (input: string): string => input.replace(
  LITERAL_TAG_RE,
  (match, slash: string, tag: string, attrs: string) => {
    if (!ALLOWED_TAG_SET.has(tag.toLowerCase())) return match;
    const decodedAttrs = attrs
      .replace(/&quot;|&#34;/g, '"')
      .replace(/&#39;|&apos;/g, "'")
      .replace(/&amp;/g, '&');
    return `<${slash}${tag}${decodedAttrs}>`;
  },
);

// HTML collapses whitespace runs and ignores newlines, so the indentation and
// blank lines the author typed vanished at render time. Encode them as markup
// (nbsp + <br>) instead of forcing `white-space: pre-wrap`, which would fight
// the node's own `nowrap` in ellipsis mode.
const NBSP = '\u00a0';

// Tags that start a new visual line — only after one of these (or a newline) is
// a run of spaces indentation rather than an ordinary word gap.
const LINE_BREAKING_TAG_RE = /^<\/?(br|hr|p|div|li|ul|ol|h[1-6]|blockquote|pre|details|summary)\b/i;

export const preserveWhitespace = (input: string): string => {
  let atLineStart = true;

  return input
    // Odd indices are the tags themselves — never rewrite inside a tag or the
    // attribute values get corrupted.
    .split(/(<[^>]*>)/)
    .map((token, i) => {
      if (i % 2 === 1) {
        if (LINE_BREAKING_TAG_RE.test(token)) atLineStart = true;
        return token;
      }
      if (!token) return token;

      const out = token
        .split(/\r\n|\r|\n/)
        .map((line, lineIndex) => {
          const indented = line.replace(/\t/g, NBSP.repeat(4));
          // A space right after an INLINE tag (`</b> c`) is a word gap, not
          // indentation — turning it into a nbsp would glue the words together.
          const isLineStart = lineIndex > 0 || atLineStart;
          return (isLineStart
            ? indented.replace(/^ +/, (spaces) => NBSP.repeat(spaces.length))
            : indented)
            .replace(/ {2,}/g, (spaces) => `${NBSP.repeat(spaces.length - 1)} `);
        })
        .join('<br>');

      atLineStart = /[\r\n]$/.test(token);
      return out;
    })
    .join('');
};

export const sanitizeRichText = (input: string): string => DOMPurify.sanitize(
  preserveWhitespace(decodeLiteralTags(input)),
  { ALLOWED_TAGS, ALLOWED_ATTR },
);

export default sanitizeRichText;
